<!-- Chat Component - Q&A Assistant -->
<div *ngIf="isOpen" class="fixed bottom-4 right-4 z-50">
  <div class="bg-white rounded-lg shadow-xl w-96 h-[500px] flex flex-col overflow-hidden border border-gray-200">
    <!-- Header -->
    <div class="flex items-center justify-between p-3 border-b border-gray-200 bg-blue-600 text-white">
      <div class="flex items-center space-x-2">
        <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            ></path>
          </svg>
        </div>
        <div>
          <h2 class="text-sm font-semibold">MediSync Assistant</h2>
          <p class="text-xs text-white/80">Ask your question</p>
        </div>
      </div>
      <button (click)="onCloseChat()" class="p-1 hover:bg-white/20 rounded-full transition-colors" aria-label="Close">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>

    <!-- Mode selector: staff vs patient assistant -->
    <div class="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
      <button
        type="button"
        (click)="setChatMode('staff')"
        [disabled]="isLoading()"
        [ngClass]="
          chatMode() === 'staff'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
        "
        class="flex-1 text-xs font-medium py-1.5 rounded-full transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Staff
      </button>
      <button
        type="button"
        (click)="setChatMode('patient')"
        [disabled]="isLoading()"
        [ngClass]="
          chatMode() === 'patient'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
        "
        class="flex-1 text-xs font-medium py-1.5 rounded-full transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Patient
      </button>
    </div>

    <!-- Messages -->
    <div #messagesContainer class="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
      <!-- Empty state -->
      <div *ngIf="messages().length === 0" class="h-full flex flex-col items-center justify-center">
        <div class="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-3">
          <svg class="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            ></path>
          </svg>
        </div>
        <p class="text-sm font-medium text-gray-700 mb-1">How can I help you?</p>
        <p class="text-xs text-gray-500 text-center">Ask a question to get started.</p>
      </div>

      <!-- Conversation -->
      <div
        *ngFor="let message of messages(); trackBy: trackByMessageId"
        class="flex"
        [ngClass]="message.role === 'user' ? 'justify-end' : 'justify-start'"
      >
        <div
          class="flex items-end space-x-2 max-w-[80%]"
          [ngClass]="message.role === 'user' ? 'flex-row-reverse space-x-reverse' : 'flex-row'"
        >
          <!-- Avatar (assistant only) -->
          <div
            *ngIf="message.role === 'assistant'"
            class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0"
          >
            <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              ></path>
            </svg>
          </div>

          <!-- Bubble -->
          <div>
            <div
              class="rounded-lg px-3 py-2 shadow-sm"
              [ngClass]="
                message.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-none'
                  : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none'
              "
            >
              <p class="text-xs whitespace-pre-wrap break-words">{{ message.content }}</p>

              <!-- Attached images (assistant answers) -->
              <div *ngIf="message.images?.length" class="mt-2 space-y-2">
                <figure *ngFor="let image of message.images">
                  <img
                    [src]="image.url"
                    [alt]="image.caption || 'Illustration'"
                    loading="lazy"
                    (click)="openImagePreview(image)"
                    class="w-full rounded-md border border-gray-200 cursor-zoom-in transition-opacity hover:opacity-90"
                  />
                  <figcaption *ngIf="image.caption" class="text-[10px] text-gray-500 mt-1">
                    {{ image.caption }}
                  </figcaption>
                </figure>
              </div>
            </div>
            <span
              class="text-[10px] text-gray-400 mt-1 block"
              [ngClass]="message.role === 'user' ? 'text-right' : 'text-left'"
            >
              {{ formatTime(message.timestamp) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Typing indicator -->
      <div *ngIf="isLoading()" class="flex justify-start">
        <div class="flex items-end space-x-2">
          <div class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              ></path>
            </svg>
          </div>
          <div class="bg-white border border-gray-100 rounded-lg rounded-bl-none px-3 py-2 shadow-sm">
            <div class="flex space-x-1">
              <span class="w-1.5 h-1.5 bg-gray-400 rounded-full typing-dot"></span>
              <span class="w-1.5 h-1.5 bg-gray-400 rounded-full typing-dot"></span>
              <span class="w-1.5 h-1.5 bg-gray-400 rounded-full typing-dot"></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Input -->
    <div class="border-t border-gray-200 p-2 bg-white">
      <div class="flex items-center space-x-2">
        <input
          type="text"
          [(ngModel)]="newMessage"
          (keypress)="handleKeyPress($event)"
          placeholder="Type your question..."
          class="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-full focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
        <button
          (click)="onSendMessage()"
          [disabled]="!newMessage().trim() || isLoading()"
          class="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          aria-label="Send"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            ></path>
          </svg>
        </button>
      </div>
    </div>
  </div>

  <!-- Image preview modal -->
  <div
    *ngIf="previewImage() as preview"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
    (click)="closeImagePreview()"
  >
    <div class="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center" (click)="$event.stopPropagation()">
      <button
        (click)="closeImagePreview()"
        class="absolute -top-3 -right-3 w-8 h-8 bg-white text-gray-700 rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
        aria-label="Close image preview"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
      <img
        [src]="preview.url"
        [alt]="preview.caption || 'Illustration'"
        class="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
      />
      <figcaption *ngIf="preview.caption" class="mt-3 text-sm text-white text-center max-w-full">
        {{ preview.caption }}
      </figcaption>
    </div>
  </div>
</div>
